import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { TableChoiceData } from '../types/questionChoices';

const gridCellSx = {
  minWidth: 0,
  p: 1,
  alignSelf: 'stretch' as const,
  display: 'flex' as const,
  alignItems: 'center' as const,
  boxShadow: (theme: { palette: { divider: string } }) =>
    `inset -1px -1px 0 0 ${theme.palette.divider}`,
};

/** Rounded corners on `letter-outline` row borders (inner cells share one rectangle per row). */
const OUTLINE_ROW_RADIUS = '8px';

export type McChoiceTableStudentProps = {
  columns: TableChoiceData['columns'];
  rows: TableChoiceData['rows'];
  selectedIndex: number | undefined;
  onSelect: (rowIndex: number) => void;
  renderCell: (html: string) => React.ReactNode;
  /** Practice mode: show green/red after “Show answer” */
  revealAnswers?: boolean;
  correctIndex?: number | null;
  /** Answer box / static preview — no selection interaction */
  readOnly?: boolean;
  /**
   * `full-row`: green/red background on the whole row when answers are shown.
   * `letter-outline`: no row fill; a 2px solid border in success/error/primary around the
   * data row (table cells). Letter is plain text with matching fill color.
   */
  answerMarks?: 'full-row' | 'letter-outline';
};

function rowOutlineColor(
  rowIdx: number,
  p: {
    outlineOnly: boolean;
    revealAnswers: boolean;
    correctIndex: number | null;
    selectedIndex: number | undefined;
    readOnly: boolean;
  },
): 'success.main' | 'error.main' | 'primary.main' | undefined {
  if (!p.outlineOnly) return undefined;
  if (p.revealAnswers && p.correctIndex != null) {
    if (rowIdx === p.correctIndex) return 'success.main';
    if (p.selectedIndex === rowIdx && rowIdx !== p.correctIndex) return 'error.main';
    return undefined;
  }
  if (!p.readOnly && p.selectedIndex === rowIdx) return 'primary.main';
  return undefined;
}

function rowLeaveHandler(rowIdx: number, setHoverRow: (v: number | null) => void) {
  return (e: React.MouseEvent) => {
    const rel = e.relatedTarget as HTMLElement | null;
    if (rel?.closest?.(`[data-mc-row="${rowIdx}"]`)) return;
    setHoverRow(null);
  };
}

/**
 * Same geometry as admin McChoiceTableGrid (two parent columns, inner table contiguous).
 * Entire row (letter + cells) is one logical choice — clicking any cell or label selects that row.
 */
export const McChoiceTableStudent: React.FC<McChoiceTableStudentProps> = ({
  columns,
  rows,
  selectedIndex,
  onSelect,
  renderCell,
  revealAnswers = false,
  correctIndex = null,
  readOnly = false,
  answerMarks = 'full-row',
}) => {
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  const n = Math.max(1, columns.length);
  const rowEndLine = rows.length + 2;

  const outlineOnly = answerMarks === 'letter-outline';

  const rowBgColor = (rowIdx: number) => {
    if (outlineOnly) return undefined;
    if (revealAnswers && correctIndex != null) {
      if (rowIdx === correctIndex) return 'success.light';
      if (selectedIndex === rowIdx) return 'error.light';
    }
    if (selectedIndex === rowIdx) return 'action.selected';
    return undefined;
  };

  /** Selection/reveal bg takes precedence; then whole-row hover (not read-only). Outline mode uses borders only. */
  const cellBg = (rowIdx: number) => {
    const base = rowBgColor(rowIdx);
    if (base !== undefined) return base;
    if (!readOnly && hoverRow === rowIdx) return 'action.hover';
    return undefined;
  };

  const outlineParams = {
    outlineOnly,
    revealAnswers,
    correctIndex,
    selectedIndex,
    readOnly,
  };

  const handleRowActivate = (rowIdx: number) => () => {
    if (!readOnly) onSelect(rowIdx);
  };

  return (
    <Box
      role={readOnly ? 'group' : 'radiogroup'}
      aria-label={readOnly ? undefined : 'Multiple choice options'}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gridTemplateRows: `repeat(${1 + rows.length}, auto)`,
        columnGap: 1.25,
        rowGap: 0,
        width: '100%',
        maxWidth: '100%',
        alignItems: 'stretch',
      }}
    >
      <Box sx={{ gridColumn: 1, gridRow: 1 }} />

      <Box
        sx={{
          gridColumn: 2,
          gridRow: `1 / ${rowEndLine}`,
          display: 'grid',
          gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
          gridTemplateRows: 'subgrid',
          gap: 0,
          columnGap: 0,
          rowGap: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          minWidth: 0,
          width: '100%',
        }}
      >
        {columns.map((column, colIdx) => (
          <Box
            key={`h-${colIdx}`}
            sx={{
              ...gridCellSx,
              fontWeight: 600,
              fontSize: '0.8125rem',
              bgcolor: 'action.hover',
            }}
          >
            {renderCell(String(column ?? ''))}
          </Box>
        ))}
        {rows.flatMap((row, i) => {
          const oc = rowOutlineColor(i, outlineParams);
          return Array.from({ length: n }, (_, j) => (
            <Box
              key={`${i}-${j}`}
              data-mc-row={i}
              tabIndex={-1}
              onClick={readOnly ? undefined : handleRowActivate(i)}
              onMouseEnter={readOnly ? undefined : () => setHoverRow(i)}
              onMouseLeave={readOnly ? undefined : rowLeaveHandler(i, setHoverRow)}
              sx={{
                ...gridCellSx,
                fontSize: '0.875rem',
                lineHeight: 1.45,
                cursor: readOnly ? 'default' : 'pointer',
                bgcolor: cellBg(i),
                ...(oc
                  ? {
                      borderTop: '2px solid',
                      borderBottom: '2px solid',
                      borderColor: oc,
                      ...(j === 0 ? { borderLeft: '2px solid', borderLeftColor: oc } : {}),
                      ...(j === n - 1 ? { borderRight: '2px solid', borderRightColor: oc } : {}),
                      ...(n === 1
                        ? { borderRadius: OUTLINE_ROW_RADIUS }
                        : {
                            ...(j === 0
                              ? {
                                  borderTopLeftRadius: OUTLINE_ROW_RADIUS,
                                  borderBottomLeftRadius: OUTLINE_ROW_RADIUS,
                                }
                              : {}),
                            ...(j === n - 1
                              ? {
                                  borderTopRightRadius: OUTLINE_ROW_RADIUS,
                                  borderBottomRightRadius: OUTLINE_ROW_RADIUS,
                                }
                              : {}),
                          }),
                      position: 'relative',
                      zIndex: 1,
                    }
                  : {}),
              }}
            >
              {renderCell(String(row[j] ?? ''))}
            </Box>
          ));
        })}
      </Box>

      {rows.map((_, i) => (
        <Box
          key={`letter-${i}`}
          data-mc-row={i}
          sx={{
            gridColumn: 1,
            gridRow: i + 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pr: 0.25,
            cursor: readOnly ? 'default' : 'pointer',
            bgcolor: cellBg(i),
            borderRadius: 1,
          }}
          role={readOnly ? undefined : 'radio'}
          aria-checked={readOnly ? undefined : selectedIndex === i}
          tabIndex={readOnly ? -1 : 0}
          onClick={readOnly ? undefined : handleRowActivate(i)}
          onMouseEnter={readOnly ? undefined : () => setHoverRow(i)}
          onMouseLeave={readOnly ? undefined : rowLeaveHandler(i, setHoverRow)}
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(i);
                  }
                }
          }
        >
          <Typography
            component="div"
            variant="body2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.35,
              color:
                revealAnswers && correctIndex != null && i === correctIndex
                  ? 'success.dark'
                  : revealAnswers && correctIndex != null && selectedIndex === i && i !== correctIndex
                    ? 'error.dark'
                    : !revealAnswers && !readOnly && selectedIndex === i
                      ? 'primary.main'
                      : 'text.primary',
            }}
          >
            {String.fromCharCode(65 + i)}.
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
